<template>
  <v-dialog v-model="dialogVisible" max-width="620" persistent>
    <v-card class="px-4">
      <v-card-title class="px-0 pt-0 pb-4 border-b d-flex align-center">
        <span class="text-lg font-bold text-gray-800">{{
          isEdit ? 'Edit Course' : 'Create New Course'
        }}</span>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon color="grey darken-2">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-0 pt-4 pb-0">
        <v-form ref="courseForm" v-model="valid" lazy-validation>
          <v-text-field
            v-model="form.title"
            label="Course Title"
            :rules="[(v) => !!v || 'Title is required']"
            outlined
            dense
            class="mt-4"
            prepend-inner-icon="mdi-book-open-variant"
          />

          <v-textarea
            v-model="form.description"
            label="Description"
            :rules="[(v) => !!v || 'Description is required']"
            outlined
            dense
            rows="3"
            prepend-inner-icon="mdi-text"
          />

          <v-select
            v-model="form.category"
            :items="categories"
            label="Category"
            :rules="[(v) => !!v || 'Category is required']"
            outlined
            dense
            prepend-inner-icon="mdi-tag-outline"
          />

          <v-select
            v-model="form.difficulty"
            :items="difficulties"
            label="Difficulty Level"
            :rules="[(v) => !!v || 'Difficulty is required']"
            outlined
            dense
            prepend-inner-icon="mdi-signal"
          />

          <v-text-field
            v-model="form.instructor"
            label="Instructor Name"
            :rules="[(v) => !!v || 'Instructor is required']"
            outlined
            dense
            class="mb-1"
            prepend-inner-icon="mdi-account-tie"
          />

          <v-text-field
            v-model="form.thumbnail"
            label="Thumbnail URL (optional)"
            outlined
            dense
            prepend-inner-icon="mdi-image-outline"
          />

          <v-autocomplete
            v-model="form.targetDepartments"
            :items="departments"
            item-text="departmentName"
            item-value="_id"
            label="Target Departments"
            multiple
            chips
            deletable-chips
            outlined
            dense
            prepend-inner-icon="mdi-briefcase-outline"
          />

          <v-autocomplete
            v-model="form.targetTeams"
            :items="teams"
            item-text="teamName"
            item-value="_id"
            label="Target Teams"
            multiple
            chips
            deletable-chips
            outlined
            dense
            prepend-inner-icon="mdi-account-group-outline"
          />

          <v-autocomplete
            v-model="form.targetUsers"
            :items="users"
            item-text="name"
            item-value="_id"
            label="Target Users"
            multiple
            chips
            deletable-chips
            outlined
            dense
            prepend-inner-icon="mdi-account-multiple-outline"
          />
        </v-form>
      </v-card-text>

      <v-card-actions class="px-0 d-flex justify-end">
        <v-spacer />
        <v-btn text class="mr-2 normal-case rounded-lg" @click="close">Cancel</v-btn>
        <v-btn
          color="#21427d"
          class="white--text normal-case px-5 rounded-lg"
          :disabled="!valid"
          @click="submit"
          depressed
        >
          <v-icon left small>{{ isEdit ? 'mdi-content-save' : 'mdi-plus' }}</v-icon>
          {{ isEdit ? 'Update' : 'Create Course' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { CATEGORIES, DIFFICULTIES, state } from './learningData'

export default {
  name: 'CourseFormDialog',
  props: {
    value: { type: Boolean, default: false },
    editData: { type: Object, default: null }
  },
  data() {
    return {
      valid: false,
      categories: CATEGORIES,
      difficulties: DIFFICULTIES,
      departments: [],
      teams: [],
      users: [],
      form: {
        title: '',
        description: '',
        category: '',
        difficulty: '',
        instructor: '',
        thumbnail: '',
        targetDepartments: [],
        targetTeams: [],
        targetUsers: []
      }
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    isEdit() {
      return !!this.editData
    }
  },
  watch: {
    async value(val) {
      if (val) {
        await this.fetchMetadata()
        if (this.editData) {
          const mappedUsers = (this.editData.targetUsers || [])
            .map((tu) => {
              return typeof tu.userId === 'object' ? tu.userId?._id : tu.userId
            })
            .filter(Boolean)

          const mappedDeps = (this.editData.targetDepartments || [])
            .map((d) => {
              return typeof d === 'object' ? d._id : d
            })
            .filter(Boolean)

          const mappedTeams = (this.editData.targetTeams || [])
            .map((t) => {
              return typeof t === 'object' ? t._id : t
            })
            .filter(Boolean)

          this.form = {
            title: this.editData.title || '',
            description: this.editData.description || '',
            category: this.editData.category || '',
            difficulty: this.editData.difficulty || '',
            instructor: this.editData.instructor || '',
            thumbnail: this.editData.thumbnail || '',
            targetDepartments: mappedDeps,
            targetTeams: mappedTeams,
            targetUsers: mappedUsers
          }
        } else {
          this.resetForm()
        }
      }
    }
  },
  methods: {
    async fetchMetadata() {
      try {
        const [depRes, teamRes] = await Promise.all([
          this.$axios.get('/orgSetup/departments'),
          this.$axios.get('/teams')
        ])
        this.departments = depRes.data.departments || []
        this.teams = teamRes.data.teams || []
        this.users = state.users || []
      } catch (err) {
        console.error('Error fetching metadata:', err)
      }
    },
    resetForm() {
      this.form = {
        title: '',
        description: '',
        category: '',
        difficulty: '',
        instructor: '',
        thumbnail: '',
        targetDepartments: [],
        targetTeams: [],
        targetUsers: []
      }
      if (this.$refs.courseForm) {
        this.$refs.courseForm.resetValidation()
      }
    },
    submit() {
      if (this.$refs.courseForm.validate()) {
        const payload = {
          ...this.form,
          targetUsers: this.form.targetUsers.map((uid) => ({ userId: uid }))
        }
        this.$emit('save', payload)
        this.close()
      }
    },
    close() {
      this.resetForm()
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.course-form-card {
  overflow: hidden;
}
.form-header {
  background: white;
}
</style>
